import axios from 'axios';
import boxImage from 'images/icon/systemSettings/box.svg';
import * as React from 'react';
import { URL_FOUNDATION } from '../../../config/keys';
import { Field } from '../../../atoms/forms';
import Dropdown from '../../../components_v2/dropdown/Dropdown';
import { DropdownData } from '../../../components_v2/dropdown/model/Model';
import { DROPDOWN_FILTER_STYLE } from '../../../components_v2/toolbarFilter/brands/BrandFilter';
import { translateToString } from '../../../styles/global/translate';
import { getProductDimensionValues } from '../../products/action';
import { ReportFiltersContext } from './generic';

// Report filters on the product custom fields: the fields flagged Product,
// whose product-level value lives in additional_values under a {p, f} path.
// The report stores the picked values per field id in
// `my_filters.product_fields`; a product passes when, for every field listed,
// its value (or one element of a Multiselect) is among the picked ones.
// goals.rs `get_product_uuids_from_filter` applies the same rule server-side.
export type ProductFieldFilters = Record<string, string[]>;
export type ProductFieldValues = Map<number, Map<string, unknown>>;

// a list of values to pick from only makes sense for these types
const FILTERABLE_TYPES = ['Text', 'Number', 'Integer', 'Boolean', 'Select', 'Multiselect'];

export function isFilterableProductField(field: Field): boolean {
	return field.is_product && FILTERABLE_TYPES.includes(field.type);
}

export function productFieldFilterIds(myFilters: { product_fields?: ProductFieldFilters } | undefined): number[] {
	return Object.keys(myFilters?.product_fields ?? {}).map(Number).filter(Number.isInteger);
}

// What a value is compared by: its text, each element on its own for a
// Multiselect. Matches `#>> '{}'` on the jsonb scalar server-side
export function productFieldValueKeys(value: unknown): string[] {
	if (value === undefined || value === null || value === '') return [];
	return (Array.isArray(value) ? value : [value])
		.filter(v => v !== undefined && v !== null && v !== '')
		.map(v => String(v));
}

export function matchProductFields(uuid: string, filters: ProductFieldFilters, values: ProductFieldValues): boolean {
	return Object.entries(filters).every(([fieldId, picked]) =>
		productFieldValueKeys(values.get(Number(fieldId))?.get(uuid)).some(k => picked.includes(k)));
}

// Cross-platform dashboards have no session cookie: they read the values with
// their own token, outside the session cache of the product page
export async function fetchProductFieldValues(fieldIds: number[], bearerToken?: string): Promise<ProductFieldValues> {
	if (fieldIds.length === 0) return new Map();
	if (!bearerToken) return getProductDimensionValues(fieldIds);
	const entries = await Promise.all(fieldIds.map(async fieldId => {
		const query = new URLSearchParams({ path: JSON.stringify({ f: fieldId }), dimensionless: 'true' }).toString();
		const rows: [Record<string, unknown>, unknown][] = await axios.get(`${URL_FOUNDATION}/api/v2/additional-values?${query}`, { headers: { Authorization: `Bearer ${bearerToken}` } }).then(r => r.data);
		const byProduct = new Map(rows
			.filter(([path]) => Object.keys(path).length === 2 && typeof path['p'] === 'string')
			.map(([path, value]) => [path['p'] as string, value]));
		return [fieldId, byProduct] as const;
	}));
	return new Map(entries);
}

// The values of the fields the report filters on, undefined while they load:
// a query must wait for them rather than filter on nothing
export function useProductFieldValues(myFilters: { product_fields?: ProductFieldFilters } | undefined): ProductFieldValues | undefined {
	const ids = productFieldFilterIds(myFilters);
	const key = JSON.stringify(ids);
	const [loaded, setLoaded] = React.useState<{ key: string, values: ProductFieldValues }>({ key: '[]', values: new Map() });
	React.useEffect(() => {
		if (ids.length === 0) return;
		let live = true;
		fetchProductFieldValues(ids)
			.then(values => { if (live) setLoaded({ key, values }); })
			.catch(e => {
				console.error(e);
				if (live) setLoaded({ key, values: new Map() });
			});
		return () => { live = false; };
	}, [key]);
	if (ids.length === 0) return loaded.values;
	return loaded.key === key ? loaded.values : undefined;
}

function valueLabel(field: Field, key: string): string {
	if (field.type === 'Boolean') return translateToString(key === 'true' ? 'yes' : 'no');
	return key;
}

// One field's picker: every value some product carries, all checked by
// default; unchecking narrows the report to the products left
export function ProductFieldFilter(props: { field: Field, fullScreen?: boolean }): JSX.Element {
	const { field } = props;
	const { filters, setFilters } = React.useContext(ReportFiltersContext);
	const picked: string[] | undefined = filters.my_filters?.product_fields?.[field.id];
	const [keys, setKeys] = React.useState<string[]>([]);

	React.useEffect(() => {
		getProductDimensionValues([field.id]).then(values => {
			const found = new Set<string>();
			values.get(field.id)?.forEach(value => productFieldValueKeys(value).forEach(k => found.add(k)));
			// a value picked earlier that no product carries any more stays
			// visible, so it can be unchecked
			picked?.forEach(k => found.add(k));
			setKeys([...found].sort((a, b) => a.localeCompare(b, undefined, { numeric: true })));
		}).catch(console.error);
	}, [field.id]);

	const list: DropdownData<string>[] = keys.map(k => ({ label: valueLabel(field, k), value: k, checked: !picked || picked.includes(k) }));

	function update(values: DropdownData<string>[]): void {
		const checked = keys.filter(k => values.some(v => v.value === k && v.checked));
		setFilters(filters => {
			const productFields: ProductFieldFilters = { ...filters.my_filters?.product_fields };
			if (checked.length === keys.length) delete productFields[field.id];
			else productFields[field.id] = checked;
			return { ...filters, my_filters: { ...filters.my_filters, product_fields: Object.keys(productFields).length > 0 ? productFields : undefined } };
		});
	}

	const amount = list.filter(l => l.checked).length;
	return <Dropdown
		dropdownStyle={{ ...DROPDOWN_FILTER_STYLE, height: '28px', inputMargin: '0 5px', inputPadding: '0 5px', width: props.fullScreen ? '100%' : DROPDOWN_FILTER_STYLE.width, containerWidth: props.fullScreen ? '100%' : DROPDOWN_FILTER_STYLE.containerWidth, optionHeight: '200px' }}
		datalist={list}
		readOnly
		checkbox
		checkboxAll
		defaultShowLabel={amount === list.length ? translateToString('all') : `${amount} / ${list.length}`}
		freezeShowLabel
		name={`dropdown_product_field_${field.id}`}
		onChange={update}
		image={boxImage}
	/>;
}
